const ANONYMOUS_ID_KEY = 'customerChatWidget:anonymousId';

const MAX_IMAGE_DIMENSION = 1280;
const IMAGE_JPEG_QUALITY = 0.72;

class CustomerChatWidget extends HTMLElement {
  #pollTimer = null;
  #threadId = null;
  #knownMessageCount = 0;
  #ready = null;
  #pendingImageData = null;
  #threadStatus = null;
  #threadRating = null;

  connectedCallback() {
    this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
    this.customerId = this.dataset.customerId || this.#getOrCreateAnonymousId();
    this.customerEmail = this.dataset.customerEmail;
    this.customerName = this.dataset.customerName;

    this.fab = this.querySelector('[data-fab]');
    this.panel = this.querySelector('[data-panel]');
    this.closeButton = this.querySelector('[data-close]');
    this.welcome = this.querySelector('[data-welcome]');
    this.chat = this.querySelector('[data-chat]');
    this.enterChatButton = this.querySelector('[data-enter-chat]');
    this.backButton = this.querySelector('[data-back]');
    this.messagesList = this.querySelector('[data-messages-list]');
    this.chatForm = this.querySelector('[data-chat-form]');
    this.chatInput = this.querySelector('[data-chat-input]');
    this.imageInput = this.querySelector('[data-chat-image]');
    this.attachPreview = this.querySelector('[data-attach-preview]');
    this.ratingBox = this.querySelector('[data-rating]');
    this.ratingStars = this.querySelectorAll('[data-rating-value]');

    this.fab?.addEventListener('click', () => this.#toggle());
    this.closeButton?.addEventListener('click', () => this.#close());
    this.enterChatButton?.addEventListener('click', () => this.#enterChat());
    this.backButton?.addEventListener('click', () => this.#exitChat());
    this.chatForm?.addEventListener('submit', (event) => this.#handleChatSubmit(event));
    this.imageInput?.addEventListener('change', (event) => this.#handleImageSelected(event));
    this.ratingStars?.forEach((btn) => btn.addEventListener('click', () => this.#submitRating(Number(btn.dataset.ratingValue))));
  }

  disconnectedCallback() {
    if (this.#pollTimer) clearInterval(this.#pollTimer);
  }

  #toggle() {
    if (this.panel.hidden) {
      this.#open();
    } else {
      this.#close();
    }
  }

  #open() {
    this.panel.hidden = false;
    this.fab.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.panel.hidden = true;
    this.fab.setAttribute('aria-expanded', 'false');
  }

  #enterChat() {
    this.welcome.hidden = true;
    this.chat.hidden = false;
    this.#ensureThread();
  }

  #exitChat() {
    this.chat.hidden = true;
    this.welcome.hidden = false;
  }

  #getOrCreateAnonymousId() {
    try {
      let id = window.localStorage.getItem(ANONYMOUS_ID_KEY);
      if (!id) {
        id = `anon-${crypto.randomUUID()}`;
        window.localStorage.setItem(ANONYMOUS_ID_KEY, id);
      }
      return id;
    } catch {
      // localStorage unavailable (private mode, etc.) — fall back to a
      // per-page-load id; replies just won't persist across visits.
      return `anon-${crypto.randomUUID()}`;
    }
  }

  #ensureThread() {
    if (!this.#ready) {
      this.#ready = this.#startThread();
    }
    return this.#ready;
  }

  async #startThread() {
    if (!this.apiBase || !this.customerId) return;

    try {
      const response = await fetch(`${this.apiBase}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: this.customerId,
          email: this.customerEmail,
          name: this.customerName,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.thread_id) return;

      this.#threadId = data.thread_id;
      await this.#loadMessages();
      this.#startPolling();
    } catch {
      // Best-effort — the widget stays empty if the service is unreachable.
    }
  }

  #handleImageSelected(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
          const scale = MAX_IMAGE_DIMENSION / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        this.#pendingImageData = canvas.toDataURL('image/jpeg', IMAGE_JPEG_QUALITY);
        if (this.attachPreview) {
          this.attachPreview.textContent = file.name;
          this.attachPreview.hidden = false;
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }

  async #handleChatSubmit(event) {
    event.preventDefault();
    const text = this.chatInput.value.trim();
    const imageData = this.#pendingImageData;
    if ((!text && !imageData) || !this.#threadId) return;

    this.chatInput.value = '';
    this.#pendingImageData = null;
    if (this.imageInput) this.imageInput.value = '';
    if (this.attachPreview) this.attachPreview.hidden = true;

    try {
      await fetch(`${this.apiBase}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread_id: this.#threadId, text, image_data: imageData }),
      });
    } catch {
      // Best-effort — the next poll will just not show the message if it truly failed.
    }

    await this.#loadMessages();
  }

  async #loadMessages() {
    if (!this.#threadId) return;

    try {
      const response = await fetch(`${this.apiBase}/api/messages?thread_id=${encodeURIComponent(this.#threadId)}`);
      const data = await response.json();
      if (!response.ok) return;

      this.#threadStatus = data.status;
      this.#threadRating = data.rating;
      this.#updateRatingPrompt();

      if (data.messages.length === this.#knownMessageCount) return;
      this.#knownMessageCount = data.messages.length;

      this.messagesList.innerHTML = data.messages
        .map(
          (m) =>
            `<li class="customer-chat-widget__message--${m.sender}">${
              m.image_data ? `<img class="customer-chat-widget__message-image" src="${this.#escape(m.image_data)}" alt="">` : ''
            }${this.#escape(m.text)}</li>`
        )
        .join('');
      this.messagesList.scrollTop = this.messagesList.scrollHeight;
    } catch {
      // Ignore — will retry on next poll tick.
    }
  }

  #updateRatingPrompt() {
    if (!this.ratingBox) return;
    const shouldShow = this.#threadStatus === 'resolved' && this.#threadRating == null;
    this.ratingBox.hidden = !shouldShow;
  }

  async #submitRating(rating) {
    if (!this.#threadId || !rating) return;

    try {
      const response = await fetch(`${this.apiBase}/api/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread_id: this.#threadId, rating }),
      });
      if (response.ok) {
        this.#threadRating = rating;
        this.#updateRatingPrompt();
      }
    } catch {
      // Best-effort — user can just leave the widget without a rating.
    }
  }

  #startPolling() {
    if (this.#pollTimer) return;
    this.#pollTimer = setInterval(() => this.#loadMessages(), 15000);
  }

  #escape(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }
}

customElements.define('customer-chat-widget', CustomerChatWidget);
